import React from "react";
import classNames from "classnames";
import { useMutation, gql } from "@apollo/client";
import { toast } from "react-toastify";
import { Grid, Form } from "semantic-ui-react";
import {
  AutoForm,
  AutoField,
  ErrorsField,
  SubmitField,
  RadioField
} from "uniforms-semantic";
import capitalize from "lodash.capitalize";

import Verify from "./Verify";
import useRoute from "../../router/useRoute";
import { cleanUpAndValidateEmail } from "/imports/utils/functions/fnCleanupEmail";
import { initJSONschema } from "/imports/utils/UI/initJSONschema";
import { signupSchema } from "./Signup.schema";
import TurnstileFormElement from "../../components/turnstile/TurnstileFormElement";
import {
  clearSessionData,
  setTurnstileToken
} from "/imports/api/accounts/client/user-session";

const CREATE_USER = gql`
  mutation createUserInForm($input: createUserInput!) {
    userId: createUser(input: $input)
  }
`;

// Enable debugging
import Debug from "debug";
const debug = Debug("user:sign-up");

// Initialize schema for the form
const schema = initJSONschema(signupSchema);

const Component = () => {
  // Clear any session data upon component load
  clearSessionData();

  // Retrieve query parameters from the route
  const { queryParams } = useRoute();

  // Mutation hook for creating a new user
  const [createUser, { data = {}, loading, error }] = useMutation(CREATE_USER);

  // Debugging mutation state
  debug("mutation data", { data, loading, error });

  // State to store the turnstile token
  const [token, setToken] = React.useState("");

  // Check if the user has been verified
  const verify = !!data.userId;

  // Form submission handler
  function onSubmitForm(formData) {
    const { email, first, last, company, type } = formData;
    
    // Prepare input data for the mutation
    const input = {
      user: {
        email: cleanUpAndValidateEmail(email),
        firstName: first,
        lastName: last
      },
      account: { company, type },
      options: { sendMail: true }
    };

    debug("new user as %o, turnstile token %o", input, token);

    // Execute mutation and handle success/error feedback
    createUser({ variables: { input } })
      .then(() => toast.success("Signup success!"))
      .catch(error => {
        debug("createUser error", error);
        toast.error("Signup failed!");
      });
  }

  // Redirect to verification screen if user creation was successful
  if (verify) return <Verify email={verify} />;

  // Initialize form model with query parameters
  const model = {
    company: queryParams.a,
    email: queryParams.e
  };

  // Display Turnstile element if token is not set
  if (!token) {
    return <TurnstileFormElement onSuccess={setToken} data-test="turnstile"  />;
  }

  // Store token in session for backend usage in headers
  debug("build form with turnstile token set");
  setTurnstileToken(token);

  return (
    <AutoForm
      className="ui inverted form sign-up"
      showInlineError
      model={model}
      error={error}
      schema={schema}
      onSubmit={onSubmitForm}
    >
      <AutoField name="company" label="Company" data-test="company" id="company" />
      
      {/* Radio field with transformed (capitalized) values */}
      <RadioField
        label="Type"
        name="type"
        transform={value => capitalize(value)}
        data-test="type"
        id="type"
      />
      
      {/* Name fields grouped */}
      <Form.Field>
        <label>Name</label>
        <Form.Group>
          <AutoField name="first" placeholder="First" data-test="firstName" id="firstName" />
          <AutoField name="last" placeholder="Surname" data-test="lastName" id="lastName" />
        </Form.Group>
        <AutoField name="email" placeholder="email" data-test="email" id="email" />
      </Form.Field>
      <ErrorsField data-test="errors" id="errors" />

      {/* Submit button and sign-in link */}
      <Grid columns={2}>
        <Grid.Column>
          <SubmitField
            className={classNames("ui primary button", { loading })}
            data-test="submit"
            id="submit"
            content={loading ? "Loading..." : "Register"}
          />
        </Grid.Column>
        <Grid.Column>
          <a href="/sign-in" data-test="signInLink" id="signInLink">Already have an account?</a>
        </Grid.Column>
      </Grid>

      {/* Privacy policy and terms */}
      <small>
        By signing up for an account you agree to the Transmate{" "}
        <a href="https://www.transmate.eu/legal/privacy" target="blank" data-test="privacyPolicy" id="privacyPolicy">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://www.transmate.eu/legal/terms-of-use" target="blank" data-test="termsOfUse" id="termsOfUse">
          Terms of Use
        </a>
        .
      </small>
    </AutoForm>
  );
};

export default Component;
